import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React from "react"

import type { CompanyDepartment, ContactQueryableFields, ContactSubscription } from "@/graphql/codegen/graphql"
import { TypeChips } from "@/screens/Companies/components/filters/company/TypeChip"
import { DepartmentChips } from "@/screens/Companies/components/filters/contact/DepartmentChip"
import { StatusChip } from "@/screens/Companies/components/filters/contact/StatusChip"
import { SubscriptionChips } from "@/screens/Companies/components/filters/SubscriptionChip"
import { useContactSubscriptionsStore } from "@/screens/Companies/stores/useContactSubscriptionsStore"
import type { TreeNode } from "@/types/tree"

export const ContactSearchFilterChips: React.FC<{ companyTypes: TreeNode<string>[] }> = ({ companyTypes }) => {
  const navigate = useNavigate({ from: "/app/companies" })
  const { isActive, companyDepartments, types, contactSubscriptions } = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const subscriptions: ContactSubscription[] = useContactSubscriptionsStore((state) => state.contactSubscriptions)

  const handleRemoveFilter = (
    key: keyof ContactQueryableFields | "types",
    value: string | CompanyDepartment | boolean
  ) => {
    void navigate({
      search: (prev: Record<string, unknown>) => {
        const newFilter = { ...prev }
        if (Array.isArray(newFilter[key])) {
          newFilter[key] = (newFilter[key] as (string | CompanyDepartment | boolean)[]).filter((item) => item !== value)
          if ((newFilter[key] as (string | CompanyDepartment | boolean)[]).length === 0) {
            delete newFilter[key] // Remove the filter entirely if it's empty
          }
        } else {
          delete newFilter[key]
        }
        return newFilter
      },
      replace: true,
    })
  }

  if (isActive === undefined && !companyDepartments && !contactSubscriptions && !types) {
    return null
  }

  return (
    <Box data-testid='contact-search-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>
      <StatusChip isActive={!!isActive} onRemove={(_, value: boolean) => handleRemoveFilter("isActive", value)} />
      <DepartmentChips
        companyDepartments={companyDepartments as CompanyDepartment[]}
        onRemove={(_, value: CompanyDepartment) => handleRemoveFilter("companyDepartments", value)}
      />

      <SubscriptionChips
        contactSubscriptions={contactSubscriptions as string[]}
        subscriptions={subscriptions}
        onRemove={(_, value: string) => handleRemoveFilter("contactSubscriptions", value)}
      />
      <TypeChips
        companyTypes={companyTypes}
        selectedTypes={types || []}
        onRemove={(_, value) => handleRemoveFilter("types", value)}
      />
    </Box>
  )
}
